import React, { useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { useDeviceVersionOptions } from '../../hooks/useDeviceVersionOptions';
import { pickDropdownId as pickId } from '../../utils/deviceVersionUtils';

function DeviceVersionFields({
  hardwareId = '',
  displaySoftwareId = '',
  deviceSoftwareId = '',
  onChange,
  required = false,
  hardwareError,
  displayError,
  firmwareError,
}) {
  const {
    hardwareList,
    displaySoftware,
    deviceSoftware,
    hardwareLoading,
    softwareLoading,
  } = useDeviceVersionOptions(hardwareId);

  // Drop values that are not returned by dropdown APIs (prevents ID/tag mismatch)
  useEffect(() => {
    if (hardwareLoading) return;

    const validHardwareId = pickId(hardwareList, hardwareId);
    if (String(validHardwareId) !== String(hardwareId || '')) {
      onChange({ hardware_id: validHardwareId, display_software_id: '', device_software_id: '' });
      return;
    }

    if (!hardwareId || softwareLoading) return;

    const validDisplayId = pickId(displaySoftware, displaySoftwareId);
    const validDeviceId = pickId(deviceSoftware, deviceSoftwareId);
    const updates = {};

    if (String(validDisplayId) !== String(displaySoftwareId || '')) {
      updates.display_software_id = validDisplayId;
    }
    if (String(validDeviceId) !== String(deviceSoftwareId || '')) {
      updates.device_software_id = validDeviceId;
    }
    if (Object.keys(updates).length > 0) onChange(updates);
  }, [
    hardwareLoading,
    softwareLoading,
    hardwareList,
    displaySoftware,
    deviceSoftware,
    hardwareId,
    displaySoftwareId,
    deviceSoftwareId,
    // onChange intentionally omitted — parent setState is stable enough for sanitize-only calls
  ]);

  const handleHardwareChange = (e) => {
    onChange({
      hardware_id: e.target.value,
      display_software_id: '',
      device_software_id: '',
    });
  };

  return (
    <>
      <Form.Group className="mb-3">
        <Form.Label>
          Hardware Version {required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Select
          name="hardware_id"
          value={hardwareId}
          onChange={handleHardwareChange}
          disabled={hardwareLoading}
          isInvalid={!!hardwareError}
        >
          <option value="">
            {hardwareLoading ? 'Loading hardware versions...' : 'Select hardware version'}
          </option>
          {hardwareList.map((item) => (
            <option key={item.id} value={item.id}>{item.version_tag}</option>
          ))}
        </Form.Select>
        {hardwareError && <Form.Control.Feedback type="invalid">{hardwareError}</Form.Control.Feedback>}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          Display Software {required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Select
          name="display_software_id"
          value={displaySoftwareId}
          onChange={(e) => onChange({ display_software_id: e.target.value })}
          disabled={!hardwareId || softwareLoading}
          isInvalid={!!displayError}
        >
          <option value="">
            {!hardwareId
              ? 'Select hardware first'
              : softwareLoading
                ? 'Loading display software...'
                : displaySoftware.length === 0
                  ? 'No compatible display versions'
                  : 'Select display software version'}
          </option>
          {displaySoftware.map((item) => (
            <option key={item.id} value={item.id}>{item.version_tag}</option>
          ))}
        </Form.Select>
        {displayError && <Form.Control.Feedback type="invalid">{displayError}</Form.Control.Feedback>}
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>
          Device / Firmware Software {required && <span className="text-danger">*</span>}
        </Form.Label>
        <Form.Select
          name="device_software_id"
          value={deviceSoftwareId}
          onChange={(e) => onChange({ device_software_id: e.target.value })}
          disabled={!hardwareId || softwareLoading}
          isInvalid={!!firmwareError}
        >
          <option value="">
            {!hardwareId
              ? 'Select hardware first'
              : softwareLoading
                ? 'Loading firmware software...'
                : deviceSoftware.length === 0
                  ? 'No compatible firmware versions'
                  : 'Select firmware software version'}
          </option>
          {deviceSoftware.map((item) => (
            <option key={item.id} value={item.id}>{item.version_tag}</option>
          ))}
        </Form.Select>
        {firmwareError && <Form.Control.Feedback type="invalid">{firmwareError}</Form.Control.Feedback>}
      </Form.Group>
    </>
  );
}

export default DeviceVersionFields;
